import { parse, Token } from "acorn";
import { analyze } from "eslint-scope";

/** Rewrite static callback-data accesses without changing unrelated JavaScript. */
export function renameConnectorFunctionReferences(
  code: string,
  oldKey: string,
  newKey: string,
  roots: readonly string[]
): string {
  try {
    const source = `(${code}\n)`;
    const tokens: Token[] = [];
    const ast: any = parse(source, {
      ecmaVersion: 2022,
      ranges: true,
      onToken: tokens,
    });
    const callback = ast.body[0]?.expression;
    if (
      !callback ||
      !["ArrowFunctionExpression", "FunctionExpression"].includes(callback.type)
    )
      return code;
    const scopes = analyze(ast, { ecmaVersion: 2022, sourceType: "script" });
    const dataIdentifiers = new Set<object>();
    for (const scope of scopes.scopes) {
      for (const reference of scope.references) {
        if (!roots.includes(reference.identifier.name)) continue;
        // Nested closures may capture data, but their own parameters/locals are unrelated.
        if (
          !reference.resolved ||
          reference.resolved.defs.every(
            (definition) =>
              definition.type === "Parameter" && definition.node === callback
          )
        )
          dataIdentifiers.add(reference.identifier);
      }
    }

    const oldPath = oldKey.split(".");
    const newPath = newKey.split(".");
    // Schema field renames keep the parent path; moving fields needs a separate migration.
    if (oldPath.length !== newPath.length) return code;
    const edits: { start: number; end: number; value: string }[] = [];
    let hasDynamicScope = false;
    function visit(node: any) {
      if (!node || typeof node !== "object") return;
      if (
        node.type === "WithStatement" ||
        (node.type === "CallExpression" &&
          node.callee.type === "Identifier" &&
          node.callee.name === "eval")
      )
        hasDynamicScope = true;
      if (node.type === "MemberExpression") {
        const path: { key: string; member: any }[] = [];
        let root = node;
        while (root.type === "MemberExpression") {
          const property = root.property;
          const key =
            !root.computed && property.type === "Identifier"
              ? property.name
              : root.computed &&
                property.type === "Literal" &&
                ["string", "number"].includes(typeof property.value)
              ? String(property.value)
              : undefined;
          if (key === undefined) break;
          path.unshift({ key, member: root });
          root = root.object;
        }
        if (
          dataIdentifiers.has(root) &&
          path.length === oldPath.length &&
          path.every((part, i) => part.key === oldPath[i])
        ) {
          path.forEach(({ key, member }, i) => {
            const renamed = newPath[i];
            if (key === renamed) return;
            const property = member.property;
            if (member.computed || /^[$A-Z_a-z][$\w]*$/.test(renamed)) {
              edits.push({
                start: property.start,
                end: property.end,
                value: member.computed ? JSON.stringify(renamed) : renamed,
              });
            } else {
              const operator =
                tokens.find(
                  (token) =>
                    token.end === property.start &&
                    [".", "?."].includes(token.type.label)
                ) ||
                tokens
                  .filter(
                    (token) =>
                      token.start >= member.object.end &&
                      token.end <= property.start &&
                      [".", "?."].includes(token.type.label)
                  )
                  .pop();
              if (!operator) throw new Error("Missing member access operator");
              edits.push({
                start: operator.start,
                end: property.end,
                value: `${member.optional ? "?." : ""}[${JSON.stringify(
                  renamed
                )}]`,
              });
            }
          });
          return;
        }
      }
      for (const value of Object.values(node)) {
        if (Array.isArray(value)) value.forEach(visit);
        else if (value && typeof value === "object") visit(value);
      }
    }
    visit(callback);
    // Direct eval/with can introduce bindings that static analysis cannot resolve.
    if (hasDynamicScope) return code;
    let result = source;
    for (const edit of edits.sort((a, b) => b.start - a.start)) {
      result =
        result.slice(0, edit.start) + edit.value + result.slice(edit.end);
    }
    parse(result, { ecmaVersion: 2022 });
    return result.slice(1, -2);
  } catch {
    // Incomplete editor code cannot be safely rewritten as JavaScript.
    return code;
  }
}
